import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loginUser } from '../api';
import styles from './HomePage.module.css';
import { Helmet } from 'react-helmet';

const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [error, setError] = useState<string | null>(null);

    const navigateToSection = (sectionId: string): void => {
        navigate(`/#${sectionId}`);
    };

    const startNewChat = (): void => {
        navigate('/chat');
    };

    const handleLogin = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        try {
            const response = await loginUser(email, password, ''); // Передаем пустой токен
            if (response.success) {
                if (response.is_active === false) {
                    navigate('/confirm-code', { state: { email } });
                } else {
                    navigate('/chat');
                }
            } else {
                setError(response.message || 'Ошибка входа');
            }
        } catch (err: any) {
            setError(err.response?.data?.error || 'Ошибка входа. Проверьте email или пароль.');
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className={styles.homeContainer}>
            <Helmet>
                <meta charSet="UTF-8" />
                <title>Вход в GPT4Chats - Доступ к ИИ-помощнику</title>
                <meta
                    name="description"
                    content="Войдите в GPT4Chats, чтобы использовать ИИ-помощник на базе GPT-4 для работы, творчества и анализа документов. Быстрый и безопасный вход."
                />
                <meta
                    name="keywords"
                    content="вход, GPT4Chats, ИИ-помощник, GPT-4, авторизация, искусственный интеллект"
                />
                <meta name="robots" content="index, follow" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <link rel="canonical" href="https://gpt4chats.ru/login" />
                <meta property="og:title" content="Вход в GPT4Chats - Доступ к ИИ-помощнику" />
                <meta
                    property="og:description"
                    content="Авторизуйтесь в GPT4Chats для доступа к мощному ИИ на базе GPT-4. Генерация текстов, анализ документов и многое другое."
                />
                <meta property="og:url" content="https://gpt4chats.ru/login" />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="GPT4Chats" />
            </Helmet>
            <header className={styles.homeHeader}>
                <div className={styles.container}>
                    <div className={styles.headerContent}>
                        <div className={styles.homeLogo}>
                            <h1>
                                GPT4Chats<span>.</span>
                            </h1>
                        </div>
                        <nav className={styles.homeNav}>
                            <button className={styles.navLink} onClick={() => navigateToSection('hero')}>
                                Главная
                            </button>
                            <button className={styles.navLink} onClick={() => navigateToSection('features')}>
                                Особенности
                            </button>
                            <button className={styles.navLink} onClick={() => navigateToSection('faq')}>
                                Вопросы
                            </button>
                            <button className={styles.startChatBtnSmall} onClick={startNewChat}>
                                Начать
                            </button>
                        </nav>
                    </div>
                </div>
            </header>

            <section className={styles.authSection}>
                <div className={styles.authContainer}>
                    <h1>Вход</h1>
                    <p className={styles.authIntro}>
                        Войдите в свой аккаунт GPT4Chats, чтобы начать использовать все возможности.
                    </p>
                    <form onSubmit={handleLogin} className={styles.authForm}>
                        <div>
                            <label htmlFor="email">Email</label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                                required
                                className={styles.authInput}
                                placeholder="Введите ваш email"
                            />
                        </div>
                        <div>
                            <label htmlFor="password">Пароль</label>
                            <input
                                type="password"
                                id="password"
                                value={password}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                                required
                                className={styles.authInput}
                                placeholder="Введите ваш пароль"
                            />
                        </div>
                        {error && <p className={styles.authError}>{error}</p>}
                        <button type="submit" className={styles.authButton}>
                            Войти
                        </button>
                    </form>

                    <div className={styles.policyFooter}>
                        <button className={styles.authLink} onClick={() => navigate('/register')}>
                            Регистрация
                        </button>
                        <span className={styles.authSep}>•</span>
                        <button className={styles.authLink} onClick={() => navigate('/forgot-password')}>
                            Забыл пароль?
                        </button>
                    </div>
                </div>
            </section>

            <footer className={styles.homeFooter}>
                <div className={styles.container}>
                    <div className={styles.footerContent}>
                        <div className={styles.footerSection}>
                            <h4>
                                GPT4Chats<span>.</span>
                            </h4>
                            <p>Ваш ИИ-помощник для работы и творчества.</p>
                            <p>GPT4Chats.ru</p>
                        </div>
                        <div className={styles.footerSection}>
                            <h4>Навигация</h4>
                            <button className={styles.navLink} onClick={() => navigateToSection('hero')}>
                                Главная
                            </button>
                            <button className={styles.navLink} onClick={() => navigateToSection('features')}>
                                Особенности
                            </button>
                            <button className={styles.navLink} onClick={() => navigateToSection('faq')}>
                                Вопросы
                            </button>
                        </div>
                        <div className={styles.footerSection}>
                            <h4>Документы</h4>
                            <button className={styles.navLink} onClick={() => navigate('/privacy-policy')}>
                                Политика конфиденциальности
                            </button>
                            <button className={styles.navLink} onClick={() => navigate('/terms-of-use')}>
                                Условия использования
                            </button>
                        </div>
                        <div className={styles.footerSection}>
                            <h4>Контакты</h4>
                            <p>Email: support@gpt4chats.ru</p>
                        </div>
                    </div>
                    <div className={styles.footerBottom}>
                        <p>© {new Date().getFullYear()} GPT4Chats. Все права защищены.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LoginPage;